import {
    Autocomplete,
    Button,
    Checkbox,
    CircularProgress,
    DialogActions,
    Grid,
    Skeleton,
    TextField,
} from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { formatDateTime, offset } from "../../utils/formatTime";
import { useFetch, useSnackbar } from "../../hooks";

import { BACKEND_ROUTES } from "../../backendRoutes";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { DYNAMIC_FEATURE_ID_FIELD_NAME } from "./constants";
import { FetchErrorAlert } from "../../components/FetchErrorAlert";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { MODEL_PROPTYPES } from "../../models";
import { PAGINATION } from "../../constants";
import PropTypes from "prop-types";
import { getSelectableMetadata } from "./utils";
import { replaceMapDataGeoJson } from "./utils/fileIoUtils";
import useSWR from "swr";
import { useState } from "react";

ParcellaireHCCLoadForm.propTypes = {
    setIsOpen: PropTypes.func.isRequired,
    parcellaireDispatch: PropTypes.func.isRequired,
    map: PropTypes.any,
    setGeoJsonKey: PropTypes.func,
    initialCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    initialContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    initialSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    initialMission: PropTypes.shape(MODEL_PROPTYPES.Mission),
    orthoimageCompany: PropTypes.shape(MODEL_PROPTYPES.Company),
    orthoimageContract: PropTypes.shape(MODEL_PROPTYPES.Contract),
    orthoimageSite: PropTypes.shape(MODEL_PROPTYPES.Site),
    orthoimageMission: PropTypes.shape(MODEL_PROPTYPES.Mission),
    setSelectableMetadata: PropTypes.func.isRequired,
    setSearchParams: PropTypes.func.isRequired,
};

export function ParcellaireHCCLoadForm({
    setIsOpen,
    parcellaireDispatch,
    map,
    setGeoJsonKey,
    initialCompany,
    initialContract,
    initialSite,
    initialMission,
    orthoimageCompany,
    orthoimageContract,
    orthoimageSite,
    orthoimageMission,
    setSelectableMetadata,
    setSearchParams,
}) {
    const [loading, setLoading] = useState(false);
    const { get } = useFetch();
    const { openSnackbar } = useSnackbar();

    const { control, setValue, handleSubmit } = useForm({
        defaultValues: {
            company: orthoimageCompany || initialCompany || null,
            contract: orthoimageContract || initialContract || null,
            site: orthoimageSite || initialSite || null,
            mission: orthoimageMission || initialMission || null,
            experiments: [],
        },
    });

    const [companyWatch, contractWatch, siteWatch, missionWatch] = useWatch({
        name: ["company", "contract", "site", "mission"],
        control,
    });

    const { data: companies, error: companiesFetchError } = useSWR(
        `${BACKEND_ROUTES.COMPANY}?sort=name&limit=${PAGINATION.COMPANY.LIMIT.MAX}`
    );
    const { data: contracts, error: contractsFetchError } = useSWR(
        companyWatch
            ? `${BACKEND_ROUTES.CONTRACT}?sort=name&companyUuid=${companyWatch.uuid}&limit=${PAGINATION.CONTRACT.LIMIT.MAX}`
            : null
    );
    const { data: sites, error: sitesFetchError } = useSWR(
        contractWatch
            ? `${BACKEND_ROUTES.SITE}?sort=name&contractUuid=${contractWatch.uuid}`
            : null
    );
    const { data: experiments, error: experimentsFetchError } = useSWR(
        siteWatch
            ? `${BACKEND_ROUTES.EXPERIMENT}?parentInfo=true&sort=name&siteUuid=${siteWatch.uuid}`
            : null
    );
    const { data: missions, error: missionsFetchError } = useSWR(
        siteWatch
            ? `${BACKEND_ROUTES.MISSION}?sort=date&siteUuid=${siteWatch.uuid}`
            : null
    );

    const mergedFetchError =
        contractsFetchError ??
        companiesFetchError ??
        sitesFetchError ??
        experimentsFetchError ??
        missionsFetchError;

    if (mergedFetchError) return <FetchErrorAlert error={mergedFetchError} />;

    if (!companies) {
        return (
            <Skeleton
                animation="wave"
                variant="rectangular"
                width="100%"
                height="20em"
            />
        );
    }

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const onSubmit = async (payload) => {
        try {
            setLoading(true);

            const featuresForParcellaire = await get(
                `${BACKEND_ROUTES.MISSION}/${payload.mission.uuid}/geojson-features?&limit=${PAGINATION.MISSION_FEATURE.LIMIT.MAX}${payload.experiments?.length > 0 ? `&${payload.experiments.map((experiment, index) => `experimentUuids[${index}]=${experiment.uuid}`).join("&")}` : ""}`,
                {
                    forwardError: true,
                }
            );

            const parcellaire = {
                type: "FeatureCollection",
                features: featuresForParcellaire.rows,
            };

            // Fetch all pages if not already the case
            if (
                PAGINATION.MISSION_FEATURE.LIMIT.MAX <
                featuresForParcellaire.count
            ) {
                const promises = [];
                for (
                    let step = PAGINATION.MISSION_FEATURE.LIMIT.MAX;
                    step < featuresForParcellaire.count;
                    step += PAGINATION.MISSION_FEATURE.LIMIT.MAX
                ) {
                    promises.push(
                        get(
                            `${BACKEND_ROUTES.MISSION}/${payload.mission.uuid}/geojson-features?offset=${step}&limit=${PAGINATION.MISSION_FEATURE.LIMIT.MAX}${payload.experiments?.length > 0 ? `&${payload.experiments.map((experiment, index) => `experimentUuids[${index}]=${experiment.uuid}`).join("&")}` : ""}`,
                            {
                                forwardError: true,
                            }
                        )
                    );
                }

                const allFeatureResults = await Promise.all(promises);

                parcellaire.features = featuresForParcellaire.rows.concat(
                    allFeatureResults.flatMap((result) => result.rows)
                );
            }

            parcellaire.features = parcellaire.features.map(
                (feature, index) => ({
                    ...feature,
                    properties: {
                        ...feature.properties,
                        [DYNAMIC_FEATURE_ID_FIELD_NAME]: `${index}`,
                    },
                })
            );

            replaceMapDataGeoJson(
                parcellaireDispatch,
                parcellaire,
                map,
                payload.mission.uuid
            );

            setGeoJsonKey(() => {
                return new Date().getTime();
            });
            setLoading(false);

            const availableMetadata = [];
            parcellaire.features.forEach(({ properties = {} }) => {
                availableMetadata.push(
                    ...Object.keys(properties).filter(
                        (property) => property !== DYNAMIC_FEATURE_ID_FIELD_NAME
                    )
                );
            });
            setSelectableMetadata(
                getSelectableMetadata([...new Set(availableMetadata)])
            );

            setSearchParams((prevState) => {
                const newSearchParams = new URLSearchParams(prevState);
                newSearchParams.set("missionUuid", payload.mission.uuid);
                newSearchParams.set("siteUuid", payload.site.uuid);
                return newSearchParams;
            });

            setIsOpen(false);
            openSnackbar("Parcellaire loaded successfully.", "success");
        } catch (error) {
            setLoading(false);
            openSnackbar(
                "Something went wrong. Please try again later.",
                "error"
            );
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} alignItems="center">
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="company"
                        render={({
                            field: { ref, onChange, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="company"
                                options={companies.rows}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value) => {
                                    onChange(value);
                                    if (
                                        value &&
                                        contractWatch?.companyUuid !==
                                            value.uuid
                                    ) {
                                        setValue("contract", null);
                                        setValue("site", null);
                                        setValue("mission", null);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Company"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="contract"
                        render={({
                            field: { ref, onChange, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="contract"
                                options={contracts?.rows ?? []}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value) => {
                                    onChange(value);
                                    if (
                                        siteWatch?.contractUuid !== value?.uuid
                                    ) {
                                        setValue("site", null);
                                        setValue("mission", null);
                                    }
                                }}
                                disabled={!companyWatch}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Campaign"
                                    />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="site"
                        render={({
                            field: { ref, onChange, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="site"
                                options={sites?.rows ?? []}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                getOptionLabel={(option) => option.name}
                                onChange={(_, value) => {
                                    onChange(value);
                                    if (
                                        missionWatch?.siteUuid !== value?.uuid
                                    ) {
                                        setValue("mission", null);
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Site"
                                    />
                                )}
                                disabled={!contractWatch || !sites}
                            />
                        )}
                    />
                </Grid>
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="experiments"
                        render={({
                            field: { ref, onChange, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                disableCloseOnSelect
                                multiple
                                id="experiments"
                                options={experiments?.rows ?? []}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                onChange={(_, value) => onChange(value)}
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option, { selected }) => (
                                    <li
                                        {...props}
                                        key={`${option.name} - ${option.Crop.name}`}
                                    >
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{
                                                marginRight: 8,
                                            }}
                                            checked={selected}
                                        />
                                        {option.name} - {option.Crop.name}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Experiments"
                                    />
                                )}
                                disabled={!siteWatch || !experiments}
                            />
                        )}
                    />
                </Grid>
                <Grid size={12}>
                    <Controller
                        control={control}
                        name="mission"
                        render={({
                            field: { ref, onChange, ...fieldProps },
                        }) => (
                            <Autocomplete
                                {...fieldProps}
                                id="mission"
                                options={missions?.rows ?? []}
                                isOptionEqualToValue={(option, value) =>
                                    option.uuid === value.uuid
                                }
                                getOptionLabel={(option) =>
                                    formatDateTime(
                                        offset(new Date(option.date), true)
                                    )
                                }
                                onChange={(_, value) => {
                                    onChange(value);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        inputRef={ref}
                                        label="Mission"
                                    />
                                )}
                                disabled={!siteWatch || !missions}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            <DialogActions>
                <Button type="button" onClick={() => setIsOpen(false)}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    startIcon={
                        loading ? (
                            <CircularProgress size="1em" />
                        ) : (
                            <FileUploadIcon />
                        )
                    }
                    type="submit"
                    disabled={loading || !missionWatch}
                >
                    Load
                </Button>
            </DialogActions>
        </form>
    );
}
